import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import { Disclaimer } from "@/components/Shared";
import { WizardProgress, FieldGrid, WizardNav, validateStep, PortalHero } from "@/components/pathways/FormBits";
import { Handshake, CheckCircle2, ArrowRight, Trophy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { title: "Agency profile", fields: [
    { key: "agency_name", label: "Agency name", required: true },
    { key: "business_reg", label: "Business registration number (optional)" },
    { key: "country", label: "Country", required: true },
    { key: "office_address", label: "Office address" },
    { key: "website", label: "Website" },
    { key: "facebook", label: "Facebook page" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "years_in_business", label: "Years in business", type: "number" },
    { key: "staff_count", label: "Number of staff", type: "select", options: ["1–5", "6–15", "16–50", "50+"] },
    { key: "branches", label: "Number of branches", type: "number" },
  ]},
  { title: "Primary contact", fields: [
    { key: "full_name", label: "Full name", required: true },
    { key: "position", label: "Position" },
    { key: "email", label: "Email", type: "email", required: true },
    { key: "phone", label: "Mobile number", required: true },
    { key: "whatsapp", label: "WhatsApp (if different)" },
    { key: "preferred_contact", label: "Preferred contact method", type: "select", options: ["Email", "Phone", "WhatsApp"] },
  ]},
  { title: "Your business", fields: [
    { key: "services_offered", label: "Services you currently offer", type: "multi", required: true,
      options: ["Student Recruitment", "Course Counselling", "Visa Documentation Support", "English Language Courses", "OSHC / Insurance", "Accommodation Assistance", "Career Counselling", "Other"] },
    { key: "recruit_countries", label: "Countries you recruit from", full: true, placeholder: "e.g. Nepal, India, Philippines, Vietnam" },
    { key: "destinations", label: "Study destinations you currently offer", type: "multi", required: true,
      options: ["Australia", "Canada", "UK", "USA", "New Zealand", "Europe", "Other"] },
    { key: "annual_students", label: "Students recruited annually", type: "select", required: true,
      options: ["Less than 20", "20–50", "50–100", "100–300", "300+"] },
    { key: "au_applications", label: "Australia / New Zealand student applications per year", type: "number" },
  ]},
  { title: "Migration & partnership", fields: [
    { key: "has_rma", label: "Do you currently have a Registered Migration Agent?", type: "select", required: true,
      options: ["Yes", "No", "We outsource migration services"] },
    { key: "rma_type", label: "If yes — in-house or external?", type: "select", options: ["In-house", "External", "Not applicable"] },
    { key: "marn", label: "MARN (optional)" },
    { key: "migration_services", label: "Migration services you're looking for", type: "multi",
      options: ["Student Visas", "Visitor Visas", "Employer Sponsorship", "Skilled Migration", "Partner Visas", "Parent Visas", "Appeals", "General Advice", "Other"] },
    { key: "partnership_interests", label: "Partnership opportunities that interest you", type: "multi",
      options: ["Student Referrals", "Migration Support", "White Label Migration Services", "Joint Marketing", "Staff Training", "Compliance Support", "University Recruitment", "Business Expansion", "CRM Integration", "API Integration"] },
  ]},
  { title: "Universities & goals", fields: [
    { key: "universities", label: "Australian & New Zealand institutions you currently represent", full: true, placeholder: "e.g. Deakin, UTS, Victoria University…" },
    { key: "goals", label: "What would you like us to help you achieve?", type: "textarea",
      placeholder: "Tell us about your growth plans, migration support needs, or partnership goals…" },
  ]},
];

export default function AgentPartnership() {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const isLast = step === STEPS.length - 1;
  const onChange = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const top = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const next = async () => {
    const missing = validateStep(STEPS[step].fields, values);
    if (missing) { toast.error(`Please complete: ${missing}`); return; }
    if (!isLast) { setStep(step + 1); top(); return; }
    if (!consent) { toast.error("Please tick the consent checkbox to submit."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/agent-partnership/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, consent: true, consent_timestamp: new Date().toISOString() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setResult(data);
      toast.success("Partnership application received!");
      top();
    } catch (err) { toast.error(err.message || "Something went wrong — please try again."); }
    finally { setLoading(false); }
  };

  if (result) {
    return (
      <div data-testid="agent-partnership-result" className="bg-white">
        <PortalHero icon={CheckCircle2} eyebrow="Application received" title="Thank you for your interest in partnering with us." subtitle="Our Partnership Team will review your application and contact you shortly." />
        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#F8F4FB] border-l-4 border-[#29324A] p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A]">Registration ID</div>
              <div className="font-display text-3xl text-[#29324A] mt-1" data-testid="agent-reference">{result.reference_id}</div>
            </div>

            <div className="mt-6 bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white p-6 md:p-8" data-testid="recommended-tier">
              <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
                <Trophy className="h-3 w-3" /> Recommended partnership
              </span>
              <div className="font-display text-3xl mt-2">{result.recommended_tier}</div>
              <div className="mt-4 text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89]">Suitable because</div>
              <ul className="mt-2 space-y-1.5">
                {result.tier_reasons.map((r) => (
                  <li key={r} className="flex gap-2 text-[13px] text-slate-100"><CheckCircle2 className="h-4 w-4 text-[#C43A89] shrink-0 mt-0.5" /> {r}</li>
                ))}
              </ul>
            </div>

            <div className="mt-6 bg-white border border-slate-200 p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#C43A89]">Recommended services</div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {result.recommended_services.map((s) => (
                  <div key={s} className="flex gap-2 text-[13px] text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {s}</div>
                ))}
              </div>
              <p className="text-[13px] text-slate-500 mt-4">A confirmation email has been sent to <strong>{values.email}</strong>.</p>
            </div>

            <div className="mt-6">
              <Link to="/book-consultation" data-testid="partnership-meeting-cta" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4">
                Book Partnership Meeting <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
        <Disclaimer />
      </div>
    );
  }

  return (
    <div data-testid="agent-partnership-page" className="bg-white">
      <PortalHero
        icon={Handshake}
        eyebrow="Education Agent Partnership Program"
        title="Become our education partner."
        subtitle="Whether you're an established education agency or just starting out, we can become your trusted Australia & New Zealand education and migration partner. Complete this short registration so we can understand your business and recommend the most suitable partnership model."
      />
      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 shadow-sm border-l-4 border-l-[#29324A] p-6 md:p-8">
            <WizardProgress titles={STEPS.map((s) => s.title)} current={step} />
            <FieldGrid fields={STEPS[step].fields} values={values} onChange={onChange} />
            {isLast && <div className="mt-6"><ConsentCheckbox value={consent} onChange={setConsent} testid="agent-consent" /></div>}
            <WizardNav onBack={() => { setStep(Math.max(0, step - 1)); top(); }} onNext={next}
              nextLabel={isLast ? "Submit Application" : "Continue"} loading={loading} showBack={step > 0} />
          </div>
        </div>
      </section>
      <Disclaimer />
    </div>
  );
}
